import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { attendanceCorrectionService } from '../services/attendanceCorrectionService';
import { getUsers } from '../services/userService';
import api from '../services/api';
import type { AttendanceCorrectionRequest } from '../types/attendanceCorrection';
import type { User } from '../types/user';
import Pagination from '../components/Pagination';
import EmploymentFilter, { type Employment } from '../components/EmploymentFilter';

interface UserPermissions {
  canApproveCorrections: boolean;
  attendanceViewScope: 'self' | 'department' | 'company' | 'custom';
  viewableUserIds?: number[];
}

const CorrectionRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employment, setEmployment] = useState<Employment>('active');
  // 初回ロードと在職状態の effect が同時に走ると同じ API を2回叩くため、初回だけ後者を止める。
  const skipFirstEmployment = useRef(true);
  const [requests, setRequests] = useState<AttendanceCorrectionRequest[]>([]);
  const [allRequests, setAllRequests] = useState<AttendanceCorrectionRequest[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  // フィルター状態（実際の値）
  const [filterStatus, setFilterStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [selectedUserId, setSelectedUserId] = useState<number | 'all'>('all');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // フィルター状態（一時値）
  const [tempFilterStatus, setTempFilterStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [tempSelectedUserId, setTempSelectedUserId] = useState<number | 'all'>('all');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('all');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [tempFilterMonth, setTempFilterMonth] = useState('');
  const [tempFilterDateFrom, setTempFilterDateFrom] = useState('');
  const [tempFilterDateTo, setTempFilterDateTo] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);

  // ページネーション状態
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // 権限チェック（役職ベース）
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const permsResponse = await api.get<UserPermissions>('/auth/permissions');
        setPermissions(permsResponse.data);

        // 承認権限がない、かつroleベースでもmanager以上でない場合はリダイレクト
        const hasRolePermission = user?.role === 'manager' || user?.role === 'admin' || user?.role === 'master';
        if (!permsResponse.data.canApproveCorrections && !hasRolePermission) {
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('権限取得エラー:', err);
        navigate('/dashboard');
      }
    };

    if (user) {
      checkPermissions();
    }
  }, [user, navigate]);

  // 初回ロード：ユーザー一覧と修正申請を取得し、デフォルト表示（全従業員、承認待ち、全期間）
  useEffect(() => {
    if (user) {
      loadInitialData();
    }
  }, [user]);

  // 在職状態を変えたら**対象者の候補だけ**取り直す。
  //
  // ここで一覧ごと読み直すと、利用者が選んだ状態（承認済み等）や期間はそのまま
  // 画面に残るのに、中身だけが既定（承認待ち・全期間）へ差し替わる。
  // 「承認済み・8月」と表示されたまま9月の承認待ちが出る、という食い違いになる。
  useEffect(() => {
    // 初回は上の初回ロードが同じ内容を取るので、ここでは走らせない（二重取得の防止）。
    if (skipFirstEmployment.current) {
      skipFirstEmployment.current = false;
      return;
    }
    if (!user) return;

    let cancelled = false;
    (async () => {
      try {
        const usersData = await getUsers(employment);
        if (cancelled) return;
        setUsers(usersData);

        // 選択中の対象者が候補から消えたら「全従業員」へ戻す。
        // select は value に一致する option が無いと**空欄ではなく先頭（＝全従業員）**を
        // 描くため、状態を戻さないと「全従業員と表示されているのに1人分しか出ない」
        // という、画面から読み取れない絞り込みが残る。
        const stillListed = (id: number | 'all') => id === 'all' || usersData.some(u => u.id === id);
        setTempSelectedUserId(prev => (stillListed(prev) ? prev : 'all'));
        setSelectedUserId(prev => (stillListed(prev) ? prev : 'all'));
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.error || '従業員一覧の取得に失敗しました');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employment, user]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // ユーザー一覧と修正申請を並行取得
      const [usersData, requestsData] = await Promise.all([
        getUsers(employment),
        attendanceCorrectionService.getRequests()
      ]);

      setUsers(usersData);
      setAllRequests(requestsData);

      // デフォルト表示：全従業員、承認待ち、全期間
      const filtered = requestsData.filter((req: any) => req.status === 'pending');
      setRequests(filtered);
    } catch (err: any) {
      setError(err.response?.data?.error || 'データの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // 表示ボタン押下時のフィルタリング
  const loadRequests = async () => {
    try {
      setIsLoading(true);
      setError('');

      // 実際の値を更新
      setFilterStatus(tempFilterStatus);
      setSelectedUserId(tempSelectedUserId);
      setFilterMonth(tempFilterMonth);
      setFilterDateFrom(tempFilterDateFrom);
      setFilterDateTo(tempFilterDateTo);

      // サーバーから最新データを取得
      const requestsData = await attendanceCorrectionService.getRequests();
      setAllRequests(requestsData);

      let filtered = [...requestsData];

      // 状態フィルター
      filtered = filtered.filter((req: any) => req.status === tempFilterStatus);

      // 従業員フィルター
      if (tempSelectedUserId !== 'all') {
        filtered = filtered.filter((req: any) => req.target_user_id === tempSelectedUserId);
      }

      // 期間フィルター（承認待ち以外の場合のみ）
      if (tempFilterStatus !== 'pending') {
        if (tempFilterMonth) {
          const [year, month] = tempFilterMonth.split('-');
          const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
          const endDate = new Date(parseInt(year), parseInt(month), 0);
          filtered = filtered.filter((req: any) => {
            const reqDate = new Date(req.date);
            return reqDate >= startDate && reqDate <= endDate;
          });
        } else if (tempFilterDateFrom && tempFilterDateTo) {
          const startDate = new Date(tempFilterDateFrom);
          const endDate = new Date(tempFilterDateTo);
          filtered = filtered.filter((req: any) => {
            const reqDate = new Date(req.date);
            return reqDate >= startDate && reqDate <= endDate;
          });
        }
      }

      setRequests(filtered);
      setCurrentPage(1); // フィルタリング時は1ページ目に戻る
    } catch (err: any) {
      setError(err.response?.data?.error || '修正申請の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    if (!window.confirm('この修正申請を承認しますか？\n承認すると自動的に勤怠記録が更新されます。')) {
      return;
    }

    try {
      setError('');
      await attendanceCorrectionService.approveRequest(id);
      setSuccessMessage('修正申請を承認しました');

      // データを再取得して再フィルタリング（loadRequestsがサーバーから取得するのでloadInitialDataは不要）
      await loadRequests();

      // 成功メッセージを3秒後に消す
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '承認に失敗しました');
    }
  };

  const handleReject = async (id: number) => {
    const reason = window.prompt('却下理由を入力してください:');
    if (!reason || !reason.trim()) {
      return;
    }

    try {
      setError('');
      await attendanceCorrectionService.rejectRequest(id);
      setSuccessMessage('修正申請を却下しました');

      // データを再取得して再フィルタリング（loadRequestsがサーバーから取得するのでloadInitialDataは不要）
      await loadRequests();

      // 成功メッセージを3秒後に消す
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '却下に失敗しました');
    }
  };

  // 部署でフィルタリングされたユーザー一覧
  const filteredUsers = users.filter(u => {
    if (selectedDepartmentId !== 'all' && u.department_id?.toString() !== selectedDepartmentId) {
      return false;
    }
    if (userSearchQuery && !u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) &&
        !u.username.toLowerCase().includes(userSearchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  // 部署一覧を取得
  const departments = Array.from(new Set(users.map(u => u.department_id).filter(Boolean)))
    .map(deptId => {
      const user = users.find(u => u.department_id === deptId);
      return { id: deptId, name: user?.department_name || '不明' };
    });

  // 終了日の最大値を計算（開始日から30日以内）
  const getMaxDateTo = () => {
    if (!tempFilterDateFrom) return '';
    const fromDate = new Date(tempFilterDateFrom);
    fromDate.setDate(fromDate.getDate() + 30);
    return fromDate.toISOString().split('T')[0];
  };

  // クリアボタンの処理
  const handleClear = () => {
    setTempFilterStatus('pending');
    setFilterStatus('pending');
    setTempSelectedUserId('all');
    setSelectedUserId('all');
    setSelectedDepartmentId('all');
    setUserSearchQuery('');
    setTempFilterMonth('');
    setFilterMonth('');
    setTempFilterDateFrom('');
    setFilterDateFrom('');
    setTempFilterDateTo('');
    setFilterDateTo('');

    // デフォルト表示に戻す（全従業員、承認待ち、全期間）
    const filtered = allRequests.filter((req: any) => req.status === 'pending');
    setRequests(filtered);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short'
    });
  };

  const formatDateTime = (dateTimeString: string | null) => {
    if (!dateTimeString) return '---';
    const date = new Date(dateTimeString);
    return date.toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: { backgroundColor: '#fff3cd', color: '#856404', text: '承認待ち' },
      approved: { backgroundColor: '#d4edda', color: '#155724', text: '承認済み' },
      rejected: { backgroundColor: '#f8d7da', color: '#721c24', text: '却下' }
    };

    const style = styles[status as keyof typeof styles] || styles.pending;

    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        ...style
      }}>
        {style.text}
      </span>
    );
  };

  const getRequestTypeBadge = (type: string) => {
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        backgroundColor: type === 'update' ? '#d1ecf1' : '#f8d7da',
        color: type === 'update' ? '#0c5460' : '#721c24'
      }}>
        {type === 'update' ? '修正' : '削除'}
      </span>
    );
  };

  if (isLoading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>読み込み中...</div>;
  }

  // 表示ボタンの有効/無効判定
  const isPeriodRequired = tempFilterStatus !== 'pending';
  const isPeriodSelected = tempFilterMonth || (tempFilterDateFrom && tempFilterDateTo);
  const isDisplayButtonEnabled = !isPeriodRequired || isPeriodSelected;

  // ページネーション用のデータ計算
  const totalPages = Math.ceil(requests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRequests = requests.slice(startIndex, endIndex);

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: 'clamp(10px, 3vw, 20px)' }}>
      <header style={{ marginBottom: 'clamp(15px, 4vw, 30px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'clamp(10px, 3vw, 20px)', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
            <h1 style={{ fontSize: 'clamp(18px, 5vw, 24px)', margin: 0 }}>打刻修正申請一覧</h1>
            {user && (
              <p style={{ margin: '5px 0 0 0', color: '#6c757d', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                {user.name}さん ({user.companyCode})
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: 'clamp(12px, 2.5vw, 14px)',
              whiteSpace: 'nowrap'
            }}
          >
            ダッシュボードに戻る
          </button>
        </div>

        {/* フィルターセクション */}
        <div style={{
          padding: 'clamp(12px, 3vw, 20px)',
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          marginBottom: 'clamp(10px, 3vw, 20px)'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: 'clamp(14px, 3.5vw, 16px)' }}>フィルター</h3>

          {/* 状態選択 */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>状態</label>
            <div style={{ display: 'flex', gap: 'clamp(5px, 1.5vw, 8px)', flexWrap: 'wrap' }}>
              {['pending', 'approved', 'rejected'].map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setTempFilterStatus(s as any);
                    // 承認待ちに変更した場合、期間フィルターをクリア
                    if (s === 'pending') {
                      setTempFilterMonth('');
                      setTempFilterDateFrom('');
                      setTempFilterDateTo('');
                    }
                  }}
                  style={{
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)',
                    backgroundColor: tempFilterStatus === s ? '#007bff' : '#e9ecef',
                    color: tempFilterStatus === s ? 'white' : '#212529',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {s === 'pending' ? '承認待ち' : s === 'approved' ? '承認済み' : '却下済み'}
                </button>
              ))}
            </div>
          </div>

          {/* 従業員選択 */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>従業員</label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {/* 部署選択 */}
              <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
                <label htmlFor="department-filter" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                  部署
                </label>
                <select
                  id="department-filter"
                  value={selectedDepartmentId}
                  onChange={(e) => setSelectedDepartmentId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)'
                  }}
                >
                  <option value="all">全部署</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id?.toString() || ''}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 在職状態 */}
          <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
            <EmploymentFilter id="corrections-employment" value={employment} onChange={setEmployment} count={users.length} layout="stacked" />
          </div>

          {/* 検索 */}
              <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
                <label htmlFor="user-search" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                  検索（氏名・ログインID）
                </label>
                <input
                  id="user-search"
                  type="text"
                  placeholder="検索..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)'
                  }}
                />
              </div>

              {/* 従業員選択 */}
              <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                <label htmlFor="user-filter" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                  選択
                </label>
                <select
                  id="user-filter"
                  value={tempSelectedUserId}
                  onChange={(e) => setTempSelectedUserId(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)'
                  }}
                >
                  <option value="all">全従業員</option>
                  {filteredUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.username})
                      {u.department_name && ` - ${u.department_name}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 期間選択（承認待ち以外の場合のみ） */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
              期間
              {tempFilterStatus === 'pending' && (
                <span style={{ marginLeft: '8px', fontSize: 'clamp(11px, 2.5vw, 12px)', fontWeight: 'normal', color: '#6c757d' }}>
                  （承認待ち時は全期間を表示します）
                </span>
              )}
            </label>
            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {/* 月選択 */}
              <div style={{ flex: '1 1 130px', minWidth: '130px' }}>
                <label htmlFor="month-filter" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                  月選択
                </label>
                <input
                  id="month-filter"
                  type="month"
                  value={tempFilterMonth}
                  onChange={(e) => {
                    setTempFilterMonth(e.target.value);
                    if (e.target.value) {
                      setTempFilterDateFrom('');
                      setTempFilterDateTo('');
                    }
                  }}
                  disabled={tempFilterStatus === 'pending' || !!tempFilterDateFrom || !!tempFilterDateTo}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    backgroundColor: (tempFilterStatus === 'pending' || tempFilterDateFrom || tempFilterDateTo) ? '#e9ecef' : 'white',
                    cursor: (tempFilterStatus === 'pending' || tempFilterDateFrom || tempFilterDateTo) ? 'not-allowed' : 'pointer'
                  }}
                />
                <div style={{ minHeight: '18px', marginTop: '2px' }}></div>
              </div>

              {/* 開始日 */}
              <div style={{ flex: '1 1 130px', minWidth: '130px' }}>
                <label htmlFor="date-from" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                  開始日
                </label>
                <input
                  id="date-from"
                  type="date"
                  value={tempFilterDateFrom}
                  onChange={(e) => {
                    setTempFilterDateFrom(e.target.value);
                    if (e.target.value) {
                      setTempFilterMonth('');
                    }
                  }}
                  disabled={tempFilterStatus === 'pending' || !!tempFilterMonth}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    backgroundColor: (tempFilterStatus === 'pending' || tempFilterMonth) ? '#e9ecef' : 'white',
                    cursor: (tempFilterStatus === 'pending' || tempFilterMonth) ? 'not-allowed' : 'pointer'
                  }}
                />
                <div style={{ minHeight: '18px', marginTop: '2px' }}></div>
              </div>

              {/* 終了日 */}
              <div style={{ flex: '1 1 130px', minWidth: '130px' }}>
                <label htmlFor="date-to" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                  終了日
                </label>
                <input
                  id="date-to"
                  type="date"
                  value={tempFilterDateTo}
                  onChange={(e) => {
                    setTempFilterDateTo(e.target.value);
                    if (e.target.value) {
                      setTempFilterMonth('');
                    }
                  }}
                  disabled={tempFilterStatus === 'pending' || !!tempFilterMonth}
                  min={tempFilterDateFrom || undefined}
                  max={getMaxDateTo() || undefined}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    backgroundColor: (tempFilterStatus === 'pending' || tempFilterMonth) ? '#e9ecef' : 'white',
                    cursor: (tempFilterStatus === 'pending' || tempFilterMonth) ? 'not-allowed' : 'pointer'
                  }}
                />
                <div style={{ minHeight: '18px', marginTop: '2px' }}>
                  {tempFilterDateFrom && tempFilterStatus !== 'pending' && !tempFilterMonth && (
                    <small style={{ color: '#6c757d', fontSize: 'clamp(10px, 2vw, 12px)' }}>
                      ※ 開始日から30日以内まで選択可能
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ボタン */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '15px', flexWrap: 'wrap' }}>
            <button
              onClick={loadRequests}
              disabled={!isDisplayButtonEnabled}
              style={{
                padding: 'clamp(8px, 2vw, 10px) clamp(18px, 4vw, 24px)',
                backgroundColor: !isDisplayButtonEnabled ? '#6c757d' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: !isDisplayButtonEnabled ? 'not-allowed' : 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                fontWeight: 'bold',
                opacity: !isDisplayButtonEnabled ? 0.6 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              表示
            </button>
            <button
              onClick={handleClear}
              style={{
                padding: 'clamp(8px, 2vw, 10px) clamp(18px, 4vw, 24px)',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                whiteSpace: 'nowrap'
              }}
            >
              クリア
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: '#d4edda',
          color: '#155724',
          border: '1px solid #c3e6cb'
        }}>
          {successMessage}
        </div>
      )}

      {requests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
          {filterStatus === 'pending' ? '承認待ち' : filterStatus === 'approved' ? '承認済み' : '却下された'}申請がありません
        </div>
      ) : (
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '600px' }}>
          <table style={{
            width: '100%',
            minWidth: '900px',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>申請日</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>対象ユーザー</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>申請者</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>タイプ</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>対象日</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>現在の打刻</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>修正後の打刻</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>理由</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>ステータス</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRequests.map((request) => (
                <tr key={request.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {formatDateTime(request.created_at)}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {(request as any).target_user_name || '-'}<br />
                    <span style={{ fontSize: 'clamp(10px, 2vw, 12px)', color: '#6c757d' }}>({(request as any).target_user_username})</span>
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {(request as any).requester_name || '-'}<br />
                    <span style={{ fontSize: 'clamp(10px, 2vw, 12px)', color: '#6c757d' }}>({(request as any).requester_username})</span>
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)' }}>
                    {getRequestTypeBadge(request.request_type)}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {request.date ? formatDate(request.date) : '-'}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(11px, 2.5vw, 13px)' }}>
                    <div>出: {formatDateTime(request.original_clock_in || null)}</div>
                    <div>退: {formatDateTime(request.original_clock_out || null)}</div>
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(11px, 2.5vw, 13px)' }}>
                    {request.request_type === 'update' ? (
                      <>
                        <div>出: {formatDateTime(request.requested_clock_in)}</div>
                        <div>退: {formatDateTime(request.requested_clock_out)}</div>
                      </>
                    ) : (
                      <span style={{ color: '#721c24' }}>削除</span>
                    )}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(11px, 2.5vw, 13px)', maxWidth: '200px' }}>
                    <div style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={request.reason}>
                      {request.reason}
                    </div>
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)' }}>
                    {getStatusBadge(request.status)}
                    {request.reviewed_at && (
                      <div style={{ fontSize: 'clamp(9px, 2vw, 11px)', color: '#6c757d', marginTop: '4px' }}>
                        {formatDateTime(request.reviewed_at)}
                        {(request as any).reviewed_by_name && (
                          <div style={{ fontSize: 'clamp(9px, 2vw, 11px)', color: '#6c757d' }}>
                            承認者: {(request as any).reviewed_by_name}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)' }}>
                    {request.status === 'pending' ? (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleApprove(request.id)}
                          style={{
                            padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: 'clamp(10px, 2vw, 12px)',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                        >
                          承認
                        </button>
                        <button
                          onClick={() => handleReject(request.id)}
                          style={{
                            padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: 'clamp(10px, 2vw, 12px)',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                        >
                          却下
                        </button>
                      </div>
                    ) : (
                      <span style={{ fontSize: 'clamp(10px, 2vw, 12px)', color: '#6c757d' }}>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ページネーション */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={requests.length}
      />
    </div>
  );
};

export default CorrectionRequestsPage;
